// src/components/roleplay/Debrief.js
// Post-call feedback screen: renders the scoreSession() result — overall
// score, per-category breakdown, summary, and a highlighted coaching tip.
import React from 'react';
import styled from 'styled-components';
import { formatUsd } from '../../config/roleplay';

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
`;

const OverallScore = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  flex-shrink: 0;
`;

const OverallNumber = styled.div`
  font-size: 40px;
  font-weight: 800;
  color: #0e8c6a;
  line-height: 1;
`;

const OverallOutOf = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #94a3b8;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HeaderTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
`;

const HeaderSub = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const Section = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 14px;
`;

const CategoryRow = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const CategoryLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
`;

const CategoryScore = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0e8c6a;
`;

const BarTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  overflow: hidden;
  margin-bottom: 8px;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: #0e8c6a;
  width: ${(props) => props.$pct}%;
`;

const CategoryRationale = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
`;

const SummaryText = styled.p`
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin: 0;
`;

const CoachingBox = styled.div`
  background: #e6f5f0;
  border: 1px solid #b8e2d5;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 20px;
`;

const CoachingLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #0e8c6a;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
`;

const CoachingText = styled.div`
  font-size: 14px;
  color: #0b5e48;
  line-height: 1.55;
  font-weight: 600;
`;

const CostNote = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin: 0 0 20px;
  text-align: right;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  appearance: none;
  border: none;
  background: #0e8c6a;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  padding: 12px 20px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: #0b7057;
  }
`;

const SecondaryButton = styled.button`
  appearance: none;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #1e293b;
  font-size: 14px;
  font-weight: 700;
  padding: 12px 20px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    border-color: #0e8c6a;
    color: #0e8c6a;
  }
`;

const SCALE_MAX = 5;

function Debrief({ result, persona, onPracticeAgain, onDone }) {
  const scores = Array.isArray(result?.scores) ? result.scores : [];
  const overall = typeof result?.overall === 'number' ? result.overall : 0;

  return (
    <div>
      <Header>
        <OverallScore>
          <OverallNumber>{overall.toFixed(1)}</OverallNumber>
          <OverallOutOf>/ {SCALE_MAX}</OverallOutOf>
        </OverallScore>
        <HeaderText>
          <HeaderTitle>Call debrief</HeaderTitle>
          <HeaderSub>
            {persona?.title ? `vs. ${persona.title}` : 'Overall performance'}
            {persona?.company ? ` at ${persona.company}` : ''}
          </HeaderSub>
        </HeaderText>
      </Header>

      {result?.coachingTip && (
        <CoachingBox>
          <CoachingLabel>Coaching tip</CoachingLabel>
          <CoachingText>{result.coachingTip}</CoachingText>
        </CoachingBox>
      )}

      {scores.length > 0 && (
        <Section>
          <SectionTitle>Scorecard</SectionTitle>
          {scores.map((s, i) => (
            <CategoryRow key={s.category || i}>
              <CategoryTop>
                <CategoryLabel>{s.category}</CategoryLabel>
                <CategoryScore>{s.score} / {SCALE_MAX}</CategoryScore>
              </CategoryTop>
              <BarTrack>
                <BarFill $pct={Math.max(0, Math.min(100, (s.score / SCALE_MAX) * 100))} />
              </BarTrack>
              {s.rationale && <CategoryRationale>{s.rationale}</CategoryRationale>}
            </CategoryRow>
          ))}
        </Section>
      )}

      {result?.summary && (
        <Section>
          <SectionTitle>Summary</SectionTitle>
          <SummaryText>{result.summary}</SummaryText>
        </Section>
      )}

      {typeof result?.costUsd === 'number' && (
        <CostNote>
          Cost this call: {formatUsd(result.costUsd)}
          {result?.usage
            ? ` · ${(result.usage.inputTokens || 0).toLocaleString()} in / ${(result.usage.outputTokens || 0).toLocaleString()} out tokens`
            : ''}
          {result?.model ? ` · ${result.model}` : ''}
        </CostNote>
      )}

      <ButtonRow>
        <SecondaryButton type="button" onClick={onDone}>
          Back to start
        </SecondaryButton>
        <PrimaryButton type="button" onClick={onPracticeAgain}>
          Practice again
        </PrimaryButton>
      </ButtonRow>
    </div>
  );
}

export default Debrief;
